// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import { updateEMRHubClinics } from "../../api";

type UpdateEMRHubClinicsPayload = {
  hubId: string;
  clinicIds: Array<string>;
};

export const useUpdateEMRHubClinics = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async ({ hubId, clinicIds }: UpdateEMRHubClinicsPayload) => {
      if (!clinicId) throw new Error();

      const token = await getToken();
      return updateEMRHubClinics(token, clinicId, hubId, clinicIds);
    },
    onSuccess: (_, { hubId }) => {
      queryClient.invalidateQueries({ queryKey: ["emr-hubs", clinicId] });
      queryClient.invalidateQueries({
        queryKey: ["emr-hubs", clinicId, hubId],
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("EMR hub clinics could not be updated!"),
          t
        ),
      });
    },
  });
};
